import { useState, useCallback } from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IDrug } from "../../../interface/GeneralResponse";
import { ExtendedSelectedDrug, FormData } from "../types/prescription.types";
import { useNavigate } from "react-router-dom";

export const usePrescriptionDrugs = (
  methods: UseFormReturn<FormData>,
  showToast: (message: string, type: "success" | "error") => void,
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedDrugs, setSelectedDrugs] = useState<ExtendedSelectedDrug[]>(
    [],
  );

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "Drugs",
  });

  const handleDrugSelect = useCallback(
    (drug: IDrug) => {
      const isCustomDrug = !drug.PackageId && !!drug.Name;
      const identifier = isCustomDrug ? drug.Name : drug.PackageId;

      if (!identifier) {
        showToast(t("invalid_drug_data"), "error");
        return;
      }

      const isAlreadySelected = selectedDrugs.some((selectedDrug) => {
        if (isCustomDrug) {
          return selectedDrug.Name === identifier;
        }
        return selectedDrug.PackageId === identifier;
      });

      if (isAlreadySelected) {
        handleRemoveDrug(identifier, isCustomDrug);
      } else {
        const uniqueId = isCustomDrug
          ? `custom_${Date.now()}_${drug.Name}`
          : drug.PackageId?.toString() || `temp_${Date.now()}`;

        const newSelectedDrug: ExtendedSelectedDrug = {
          ...drug,
          PackageId: drug.PackageId,
          Name: drug.Name,
          DrugName: drug.DrugName || drug.Name || "Unknown Drug",
          FacilityName: (drug as any).Facility || drug.FacilityName || "",
          DosageForm: drug.DosageForm || "",
          _tempId: uniqueId,
          isCustom: isCustomDrug,
        };

        setSelectedDrugs((prev) => [...prev, newSelectedDrug]);

        const formData: any = {
          Frequency: "",
          SelectedDays: [],
          Meal: "",
          Sleep: false,
          Times: null,
          Duration: null,
          Instructions: "",
          DosageNum: drug.DosageNum || null,
          DrugName: drug.DrugName || null,
          DosageUnit: drug.DosageUnit || undefined,
        };

        if (isCustomDrug) {
          formData.Name = drug.Name;
          formData._tempId = uniqueId;
        } else {
          formData.PackageId = drug.PackageId;
        }

        append(formData);

        const displayName = drug.Name || drug.DrugName || "";
        showToast(`${displayName} ${t("added_to_selected_drugs")}`, "success");
      }
    },
    [selectedDrugs, showToast, t, append],
  );

  const handleRemoveDrug = useCallback(
    (identifier: string | number, isCustomDrug: boolean = false) => {
      const drugToRemove = selectedDrugs.find((d) => {
        if (isCustomDrug) {
          return d.Name === identifier || d._tempId === identifier;
        }
        return d.PackageId === identifier;
      });

      if (drugToRemove) {
        setSelectedDrugs((prev) =>
          prev.filter((drug) => {
            if (isCustomDrug) {
              return drug.Name !== identifier && drug._tempId !== identifier;
            }
            return drug.PackageId !== identifier;
          }),
        );

        const index = fields.findIndex((field: any) => {
          if (isCustomDrug) {
            return field.Name === identifier || field._tempId === identifier;
          }
          return field.PackageId === identifier;
        });

        if (index !== -1) {
          remove(index);
        }

        const displayName = drugToRemove.Name || drugToRemove.DrugName || "";
        showToast(
          `${displayName} ${t("removed_from_selected_drugs")}`,
          "success",
        );
      }
    },
    [selectedDrugs, fields, remove, showToast, t],
  );

  const resetDrugs = useCallback(() => {
    setSelectedDrugs([]);
  }, []);

  const setInitialDrugs = useCallback((drugs: any[]) => {
    setSelectedDrugs(drugs);
  }, []);

  return {
    selectedDrugs,
    fields,
    handleDrugSelect,
    handleRemoveDrug,
    resetDrugs,
    setInitialDrugs,
  };
};
